import { Box } from '@material-ui/core'
import { Coupon, Organization } from '@noco/http-client/lib/noco'
import {
  OrganizationPaymentContractBasicInfo,
  OrganizationPaymentContractPaidInfo,
  OrganizationPlanDetail,
  OrganizationStatusEnum
} from '@noco/http-client/lib/noco'
import { useRouter } from 'next/router'
import React, { useCallback, useMemo, useState } from 'react'
import { Stepper } from 'src/components/molecules/Stepper'
import { StepperBoard } from 'src/components/molecules/StepperBoard'
import { StepperContext } from 'src/components/molecules/StepperContext'
import { pushUserActionDataLayer } from 'src/utils/gtm'
import { PaymentMethodProps } from '../PaymentMethodCard'
import { ContractFirstStep } from './ContractFirstStep'
import { ContractFourthStep } from './ContractFourthStep'
import { ContractSecondStep } from './ContractSecondStep'
import { ContractThirdStep } from './ContractThirdStep'

export interface ContractFreePaidStepperProps {
  plan: OrganizationPlanDetail
  organization: Organization
}

export const ContractPaidPlanStepper = ({ plan, organization }: ContractFreePaidStepperProps) => {
  const router = useRouter()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [userOptionQuantity, setUserOptionQuantity] = useState(0)
  const [contractBasicInfo, setContractBasicInfo] = useState<OrganizationPaymentContractBasicInfo>()
  const [contractPaidInfo, setContractPaidInfo] = useState<OrganizationPaymentContractPaidInfo>()
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodProps>()
  const [coupon, setCoupon] = useState<Coupon | undefined>()

  const prevStep = useCallback(() => {
    if (currentIndex <= 0) return

    window.scrollTo({ top: 0 })
    setCurrentIndex(value => value - 1)
  }, [currentIndex])

  const nextStep = useCallback(() => {
    window.scrollTo({ top: 0 })
    setCurrentIndex(value => value + 1)
  }, [])

  const isContract = useMemo(() => organization.status === OrganizationStatusEnum.Trialing, [organization])
  const isResumed = useMemo(() => organization.status === OrganizationStatusEnum.Paused, [organization])

  const contractFirstStep = useMemo(() => {
    return (
      <ContractFirstStep
        plan={plan}
        onSubmit={userOptionQuantity => {
          setUserOptionQuantity(userOptionQuantity)
          nextStep()
        }}
      />
    )
  }, [nextStep, plan])

  const contractSecondStep = useMemo(() => {
    return (
      <ContractSecondStep
        plan={plan}
        backButtonDisabled={!plan.optionUserPlan}
        onSubmit={(contractBasicInfo, contractPaidInfo, paymentMethod, coupon) => {
          setContractBasicInfo(contractBasicInfo)
          setContractPaidInfo({ ...contractPaidInfo, consent: false })
          setPaymentMethod(paymentMethod)
          setCoupon(coupon)
          nextStep()
        }}
        onBack={() => prevStep()}
        isResumed={isResumed}
      />
    )
  }, [nextStep, plan, prevStep, isResumed])

  const contractThirdStep = useMemo(() => {
    return (
      <ContractThirdStep
        plan={plan}
        userOptionQuantity={userOptionQuantity}
        contractBasicInfo={contractBasicInfo}
        contractPaidInfo={contractPaidInfo}
        paymentMethod={paymentMethod}
        coupon={coupon}
        onSubmit={() => {
          pushUserActionDataLayer('contract', 'completed_contract', 'paid_plan')
          nextStep()
        }}
        onBack={() => prevStep()}
      />
    )
  }, [contractBasicInfo, contractPaidInfo, coupon, nextStep, paymentMethod, plan, prevStep, userOptionQuantity])

  const items = useMemo(() => {
    const items = []

    // ユーザー追加オプションの利用が可能なプランの場合
    if (plan.optionUserPlan) {
      items.push({
        label: 'ユーザーご利用枠の追加',
        renderItem: contractFirstStep
      })
    }

    items.push(
      {
        label: 'お支払い情報を入力',
        renderItem: contractSecondStep
      },
      {
        label: '確認',
        renderItem: contractThirdStep
      },
      {
        label: '完了',
        renderItem: (
          <ContractFourthStep
            isContract={isContract}
            onConfirm={() => {
              router.push('/setting/team/contract')
            }}
          />
        )
      }
    )

    return items
  }, [plan.optionUserPlan, contractSecondStep, contractThirdStep, isContract, contractFirstStep, router])

  return (
    <Box>
      <StepperContext currentIndex={currentIndex}>
        <Stepper items={items} onClick={idx => setCurrentIndex(idx)} />
        <StepperBoard boardItems={items} />
      </StepperContext>
    </Box>
  )
}
